import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ServicesHero from './ServicesHero';

const RadiologyServiceDetail = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();

  const radiologyServices = {
    'ct-scan': {
      name: 'CT Scan',
      image: '/ct-scan.jpg',
      description: 'Computed Tomography (CT) scanning uses X-rays and computer technology to create detailed cross-sectional images of the body. CT scans are invaluable for diagnosing a wide range of conditions including injuries, tumors, infections, and internal bleeding.',
      details: [
        'High-resolution imaging for accurate diagnosis',
        'Fast scanning times for patient comfort',
        'Advanced 3D reconstruction capabilities',
        'Low radiation dose protocols available',
        'Expert radiologists for image interpretation'
      ],
      uses: [
        'Diagnosing injuries and trauma',
        'Detecting tumors and cancers',
        'Identifying infections and inflammation',
        'Evaluating blood vessels',
        'Planning surgical procedures',
        'Monitoring treatment effectiveness'
      ]
    },
    'x-ray': {
      name: 'X-ray',
      image: '/x-ray.jpg',
      description: 'X-ray imaging is one of the most common and oldest forms of medical imaging. It uses electromagnetic radiation to create images of internal structures, particularly bones. X-rays are essential for diagnosing fractures, infections, arthritis, and lung conditions.',
      details: [
        'Quick and painless imaging procedure',
        'Digital X-ray technology for instant results',
        'Minimal radiation exposure',
        'Suitable for all age groups',
        'Wide range of applications'
      ],
      uses: [
        'Detecting bone fractures',
        'Diagnosing lung conditions',
        'Identifying dental problems',
        'Evaluating joint conditions',
        'Screening for certain diseases',
        'Monitoring healing progress'
      ]
    },
    'ultrasound': {
      name: 'Ultra sound',
      image: '/ultrasound.jpg',
      description: 'Ultrasound imaging uses high-frequency sound waves to create real-time images of internal organs and structures. It is a safe, non-invasive procedure with no radiation exposure. Ultrasound is commonly used for pregnancy monitoring, abdominal imaging, and vascular studies.',
      details: [
        'No radiation exposure - completely safe',
        'Real-time imaging capabilities',
        'Ideal for soft tissue visualization',
        'Portable equipment for bedside imaging',
        'Color Doppler for blood flow assessment'
      ],
      uses: [
        'Pregnancy monitoring and fetal development',
        'Abdominal organ evaluation',
        'Vascular and blood flow studies',
        'Breast imaging',
        'Musculoskeletal assessment',
        'Guiding medical procedures'
      ]
    },
    'mri': {
      name: 'MRI',
      image: '/mri.jpg',
      description: 'Magnetic Resonance Imaging (MRI) uses powerful magnets and radio waves to produce detailed images of organs and tissues. MRI provides excellent soft tissue contrast and is particularly useful for imaging the brain, spine, joints, and internal organs without using ionizing radiation.',
      details: [
        'No ionizing radiation - safe imaging',
        'Superior soft tissue visualization',
        'Multi-planar imaging capabilities',
        'Advanced contrast techniques available',
        'High-field strength for better image quality'
      ],
      uses: [
        'Brain and neurological imaging',
        'Spinal cord and spine evaluation',
        'Joint and musculoskeletal imaging',
        'Abdominal and pelvic organ assessment',
        'Cardiac imaging',
        'Breast cancer screening'
      ]
    }
  };

  const service = radiologyServices[serviceSlug];

  if (!service) {
    return (
      <Box>
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Service Not Found
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/services/radiology')}
            sx={{ mt: 2 }}
          >
            Back to Radiology Services
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <ServicesHero 
        title={service.name}
        subtitle="Advanced diagnostic imaging service"
        serviceType="radiology"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/services/radiology')}
          sx={{ mb: 4 }}
        >
          Back to Radiology Services
        </Button>

        {/* Service Details */}
        <Grid container spacing={4}>
          {/* Image Section */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 4 }}>
              <CardMedia
                component="img"
                image={service.image}
                alt={service.name}
                sx={{
                  width: '100%',
                  height: 400,
                  objectFit: 'cover',
                  backgroundColor: '#f5f5f5'
                }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
                }}
              />
            </Card>
          </Grid>

          {/* Content Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              {service.name}
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
              {service.description}
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Key Features:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {service.details.map((detail, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body1"
                    sx={{
                      mb: 1.5,
                      lineHeight: 1.8,
                      color: 'text.secondary'
                    }}
                  >
                    {detail}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Uses Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Common Uses
          </Typography>
          <Grid container spacing={2}>
            {service.uses.map((use, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Chip
                  label={use}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    py: 2,
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      textAlign: 'center'
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', 
          borderRadius: 3, 
          textAlign: 'center',
          color: 'white'
        }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>
            Schedule Your {service.name} Appointment
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.8, color: 'rgba(255,255,255,0.9)' }}>
            Contact us to schedule your {service.name.toLowerCase()} appointment and receive high-quality diagnostic imaging services.
          </Typography>
          <Chip 
            label="Contact Us" 
            sx={{ 
              fontSize: '1.1rem', 
              py: 1,
              mt: 2,
              backgroundColor: 'white',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
            onClick={() => navigate('/contact')}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default RadiologyServiceDetail;

